import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorDetail, errorFields, errorMessage, hasCode, messageForCode } from '../lib/api-error';
import { useAuth } from '../lib/auth-context';
import { hasArea, isReadOnly } from '../lib/permissions';
import { date, dateTime, money } from '../lib/format';
import { toast } from '../lib/toast';
import { DOCUMENT_TYPE_LABELS, STATUS_LABELS, type SalesInvoice } from '../lib/invoices-api';
import {
  ABANDONABLE_STATUSES,
  EINVOICE_BUSINESS_RESPONSE_LABELS,
  EINVOICE_FAILURE_CLASS_LABELS,
  EINVOICE_LOOKUP_RESULT_LABELS,
  EINVOICE_NETWORK_LABELS,
  EINVOICE_OBLIGATION_BASIS_LABELS,
  EINVOICE_STATUS_LABELS,
  EINVOICE_STATUS_MEANINGS,
  EINVOICE_STATUS_TONES,
  EINVOICE_TRANSPORT_KIND_LABELS,
  INCOMING_VERIFICATION_LABELS,
  MANUAL_MARKABLE_STATUSES,
  PEPPOL_MODE_LABELS,
  PEPPOL_MODE_MEANINGS,
  QUEUE_PAGE_SIZE,
  SENDABLE_STATUSES,
  daysOpen,
  downloadEinvoiceXml,
  saveBlob,
  useAbandonEinvoice,
  useEinvoiceInbox,
  useEinvoiceQueue,
  useEinvoiceTransmission,
  useEinvoiceTransmissions,
  useMarkSentManually,
  usePrepareEinvoice,
  useSendEinvoice,
  useUploadIncomingUbl,
  type EinvoiceDocument,
  type EinvoiceTransmissionView,
  type IncomingDocument,
  type PeppolManualHandoff,
} from '../lib/einvoice-api';
import { useCreatePurchaseFromIncoming } from '../lib/purchases-api';
import type { SettingsResponse } from './settings-types';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Spinner,
  Table,
  Td,
  Textarea,
  Th,
} from '../components/ui';

/**
 * E-fatura (Peppol) ekranı — aynı hukuki kanalın İKİ YÖNÜ.
 *
 * ── SAYFANIN VAR OLMA SEBEBİ ──────────────────────────────────────────────
 * Tek bir soruyu cevaplar: "yasal olarak iletilmesi gereken ama iletilememiş
 * belgem var mı?" Bu, sunucudaki `obligationOpen` bayrağının (`einvoiceRequired
 * = true AND einvoiceDocumentId IS NULL`) ekrandaki karşılığıdır. Yükümlülük
 * yalnız GERÇEK bir başarıda kapanır; "gönderdim galiba" diye kapanan hiçbir
 * yol yoktur.
 *
 * ── ÜÇ ŞEY EKRANDA AÇIKÇA YAZILI OLMAK ZORUNDA ────────────────────────────
 *  1. `SUBMITTED` (sağlayıcı taşımaya aldı — TESLİM DEĞİL) ile `DELIVERED`
 *     (ağ teslim etti) farkı. Aradaki fark tam olarak "gönderdim mi"
 *     sorusunun cevabıdır ve saatler sürebilir.
 *  2. Elle rejimde XML İNDİRMEK, teslimatın kendisidir. Teslim edilmiş bir
 *     belgede sunucu 409 `EINVOICE_ALREADY_DELIVERED` döndürür ve panel bunu
 *     bir ONAY adımına çevirir — ikinci yükleme alıcıda İKİNCİ BİR BORÇ KAYDI
 *     demektir.
 *  3. "Elle gönderildi" beyanı GERİ ALINAMAZ: kolonlar KİLİT, durum TERMİNAL,
 *     LIVE ağda faturaya MÜHÜR yazılır. Yanlış işaretlenen belge hukuki alarm
 *     sorgusunda bir daha ASLA görünmez.
 *
 * ── GELEN KUTUSU NEDEN AYNI SAYFADA ───────────────────────────────────────
 * Giden ve gelen, aynı hukuki kanalın iki yönüdür; ayrı sayfalar operatöre iki
 * ayrı sistem varmış hissi verirdi.
 *
 * ⚠ Gelen belge iki yoldan girer: sağlayıcının imzalı webhook'u ve — Peppol
 * aboneliği olmadığı için asıl kullanılan — operatörün YÜKLEDİĞİ UBL dosyası.
 * Satır hangisinden geldiğini söyler; ikisi aynı şey değildir.
 *
 * ⚠ GELEN TARAFTA TUTAR, KDV ya da "indirilebilir KDV" GÖSTERİLMEZ: bir belge
 * alınmış olmak onaylanmış olmak değildir ve ekranda görünen bir rakam
 * doğrulanmış sanılır. Rakamlar taslak alışa çevrildikten sonra, onay akışının
 * içinde görünür.
 */

type Tab = 'outbox' | 'inbox';

export function EinvoicePage() {
  const { user } = useAuth();
  const readOnly = isReadOnly(user);
  /*
   * ⚠ Kuyruk `GET /invoices` üzerinden kuruluyor ve o uç `@AreaAuth('invoices')`
   * taşıyor. Yalnız `einvoice` yetkisi olan bir kullanıcı için istek 403
   * dönerdi; sebebini söylemeyen bir hata yerine isteği hiç atmıyoruz.
   */
  const canReadInvoices = hasArea(user, 'invoices');
  const canReadSettings = hasArea(user, 'settings');

  /*
   * ⚠⚠ DERİN BAĞLANTI: `/einvoice?invoice=<id>`. Fatura detayından buraya
   * gelen operatör "Bekleyenler" listesinde arama yapmak ZORUNDA KALMAMALI —
   * üstelik bir belge iletilmeye BİR KEZ başladıktan sonra (`einvoiceDocumentId`
   * dolduğu an) bu listeden TAMAMEN DÜŞER (`openObligations` yalnız hiç
   * hazırlanmamışı sayar). Yani reddedilmiş ya da yeniden denenmesi gereken bir
   * taşımayı bulmanın panelde BAŞKA hiçbir yolu yok; parametre olmadan operatör
   * o belgeye bir daha asla ulaşamazdı.
   *
   * ⚠ `TransmissionDetail` seçili KİMLİĞE bakar, görünür sayfadaki satıra değil
   * — yani liste dışında kalan bir belge de burada sorunsuz açılır.
   */
  const [params] = useSearchParams();
  const linkedInvoiceId = params.get('invoice');

  const [tab, setTab] = useState<Tab>('outbox');
  const [selected, setSelected] = useState<string | null>(linkedInvoiceId);

  /*
   * ⚠ YALNIZ İLK RENDER'DA state'i doldurmak yetmez: sayfa zaten AÇIKKEN
   * ikinci bir fatura detayından ikinci bir derin bağlantıya tıklanırsa rota
   * bileşeni YENİDEN KURULMAZ (aynı rota, yalnız sorgu dizesi değişir) ve
   * `useState`in ilk değeri bir daha okunmaz — seçim eski faturada asılı
   * kalırdı.
   */
  useEffect(() => {
    if (linkedInvoiceId !== null) setSelected(linkedInvoiceId);
  }, [linkedInvoiceId]);

  return (
    <>
      <PageHeader
        title="E-fatura (Peppol)"
        description="Yükümlülük yalnız gerçek bir iletimle kapanır: sağlayıcının belgeyi alması TESLİM DEĞİLDİR."
      />

      <PeppolConfigBand canRead={canReadSettings} />

      <nav className="mb-4 flex gap-2" aria-label="E-fatura bölümleri">
        <TabButton active={tab === 'outbox'} onClick={() => setTab('outbox')}>
          Gönderim bekleyenler
        </TabButton>
        <TabButton active={tab === 'inbox'} onClick={() => setTab('inbox')}>
          Gelen kutusu
        </TabButton>
      </nav>

      {tab === 'outbox' ? (
        <OutboxTab
          canReadInvoices={canReadInvoices}
          readOnly={readOnly}
          selected={selected}
          onSelect={setSelected}
        />
      ) : (
        <InboxTab canReadSettings={canReadSettings} readOnly={readOnly} />
      )}
    </>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      className={
        'rounded-[var(--r-md)] border px-3 py-1.5 text-sm font-medium transition-colors ' +
        (active
          ? 'border-transparent bg-[var(--c-brand)] text-white'
          : 'border-[var(--c-border-strong)] bg-[var(--c-surface)] text-[var(--c-ink)] hover:bg-[var(--c-surface-alt)]')
      }
    >
      {children}
    </button>
  );
}

// ───────────────────────────────────────────────────────── yapılandırma bandı

/**
 * Kanalın YAPILANDIRMA durumu — belgeye donmuş değerlerle KARIŞTIRILMAZ.
 *
 * Band ayarların BUGÜNKÜ hâlini gösterir ("kanal açık mı"); bir belgenin
 * detayındaki ağ/sağlayıcı ise o belgeye DONMUŞ anlık görüntüdür ("bu baytlar
 * nereye gitti"). İki farklı soru, iki farklı kaynak — tek bir yerde
 * birleştirilseydi, mod LIVE'a alındığı gün eski TEST satırları gerçek
 * gönderilmiş gibi okunurdu.
 *
 * ⚠ `peppol_mode` ve `peppol_webhook_secret` ADMIN-ONLY ayarlardır ve
 * `GET /settings` onları başka rollere HİÇ döndürmez; ayrıca ucun kendisi
 * `@AreaAuth('settings')` ile korunur. Yetki yoksa istek atılmaz ve band
 * "bilinmiyor" der — 403 üretip operatöre "bir şeyler bozuk" hissi vermek
 * yerine.
 */
function PeppolConfigBand({ canRead }: { canRead: boolean }) {
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsResponse>('/settings'),
    enabled: canRead,
  });

  if (!canRead) return null;
  if (settings.isPending || settings.isError) return null;

  const values = settings.data.values;
  const rawMode = values.peppol_mode;
  const mode =
    rawMode === 'TEST' || rawMode === 'LIVE' || rawMode === 'OFF' ? rawMode : ('OFF' as const);
  const provider = typeof values.peppol_provider === 'string' ? values.peppol_provider : '';
  const webhookSet = values.peppol_webhook_secret_isSet === true;

  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
        <span className="font-medium">Peppol modu:</span>
        <Badge tone={mode === 'LIVE' ? 'ok' : mode === 'TEST' ? 'warn' : 'neutral'}>
          {PEPPOL_MODE_LABELS[mode]}
        </Badge>
        <span className="text-[var(--c-ink-soft)]">{PEPPOL_MODE_MEANINGS[mode]}</span>
        <span className="ml-auto text-xs text-[var(--c-ink-faint)]">
          Sağlayıcı: {provider === '' ? 'seçilmedi (elle taşıma)' : provider}
        </span>
      </div>

      {!webhookSet && (
        <div className="border-t border-[var(--c-border)] px-4 py-3 text-sm text-[var(--c-danger)]">
          <strong>Webhook imza sırrı girilmemiş.</strong> Gelen e-faturalar bu sır olmadan
          doğrulanamaz ve uç onları kabul etmez; sağlayıcı bir süre yeniden dener, deneme bütçesi
          dolduğunda belge KAYBOLUR. Ayarlar → Peppol bölümünden sırrı girin.
        </div>
      )}
    </Card>
  );
}

// ──────────────────────────────────────────────────── sekme 1: bekleyenler

function OutboxTab({
  canReadInvoices,
  readOnly,
  selected,
  onSelect,
}: {
  canReadInvoices: boolean;
  readOnly: boolean;
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [page, setPage] = useState(1);
  const queue = useEinvoiceQueue(canReadInvoices);

  // `?? []` her render'da YENİ bir dizi üretir; memo'suz olsaydı aşağıdaki
  // `visible` ve `ids` her render'da değişir, satır başı durum istekleri yeniden tetiklenirdi.
  const items = useMemo(() => queue.data?.items ?? [], [queue.data]);
  const pageCount = Math.max(1, Math.ceil(items.length / QUEUE_PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const visible = useMemo(
    () => items.slice((current - 1) * QUEUE_PAGE_SIZE, current * QUEUE_PAGE_SIZE),
    [items, current],
  );

  /*
   * Durum rozetleri SATIR BAŞINA bir istekle geliyor — bugünkü API'de taşıma
   * satırlarını toplu döndüren bir uç yok. Fan-out bu yüzden yalnız GÖRÜNEN
   * sayfaya uygulanır (bkz. `useEinvoiceTransmissions`).
   */
  const ids = useMemo(() => visible.map((i) => i.id), [visible]);
  const transmissions = useEinvoiceTransmissions(ids);

  if (!canReadInvoices) {
    return (
      <Card>
        <EmptyState
          title="Bekleyenler listesi için fatura yetkisi gerekiyor"
          description="Liste satış belgeleri ucundan kuruluyor; bu bölüme yetkiniz olmadan yükümlülüğü açık belgeler hesaplanamaz. Tek tek belge detayları e-fatura yetkisiyle görülebilir."
        />
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-4">
        <CardHeader
          title="Gönderim bekleyenler"
          description="Yükümlülüğü olan ve HÂLÂ iletilememiş belgeler; en uzun süredir bekleyen en üstte."
        />

        {queue.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner size={24} />
          </div>
        ) : queue.isError ? (
          <ErrorState message={errorMessage(queue.error)} detail={errorDetail(queue.error)} />
        ) : items.length === 0 ? (
          <EmptyState
            title="Bekleyen belge yok"
            description="Yükümlülüğü olan her belge için bir iletim mührü var."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Belge</Th>
                  <Th>Müşteri</Th>
                  <Th>Düzenlenme</Th>
                  <Th align="right">Kaç gündür açık</Th>
                  <Th align="right">Tutar</Th>
                  <Th>Taşıma durumu</Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((invoice) => (
                  <QueueRow
                    key={invoice.id}
                    invoice={invoice}
                    view={transmissions.byInvoice.get(invoice.id) ?? null}
                    loading={transmissions.isPending}
                    selected={selected === invoice.id}
                    onSelect={() => onSelect(selected === invoice.id ? null : invoice.id)}
                  />
                ))}
              </tbody>
            </Table>

            <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--c-border)] px-4 py-3 text-xs text-[var(--c-ink-soft)]">
              <span>
                {/*
                  Sayı artık SUNUCUNUN saydığıdır. Önceden "kaç belge tarandı"
                  yazıyordu; o rakam operatöre listenin eksik olabileceğini
                  hatırlatmak için vardı ve artık eksik değil.
                */}
                {items.length} / {queue.data?.total ?? 0} belge bekliyor
              </span>
              <span className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={current <= 1}
                  onClick={() => setPage(current - 1)}
                >
                  Önceki
                </Button>
                <span>
                  {current} / {pageCount}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={current >= pageCount}
                  onClick={() => setPage(current + 1)}
                >
                  Sonraki
                </Button>
              </span>
            </footer>
          </>
        )}

        {/*
          ⚠ "Yalnız en yeni N belge tarandı" uyarısı KALDIRILDI çünkü artık
          doğru değil: liste `GET /einvoice/queue` ile sunucudan geliyor ve
          tam. Eski uyarı dürüsttü ama sorunu çözmüyordu — taramanın dışında
          kalanlar EN ESKİ, yani en uzun süredir iletilememiş, en riskli
          belgelerdi. Kural (`einvoiceRequired AND einvoiceDocumentId IS NULL`)
          artık veritabanında sorgulanıyor.
        */}
      </Card>

      {/*
        ⚠ `key` ZORUNLU: başka bir belgeye geçildiğinde bileşen yeniden kurulmalı.
        Aynı örnek yeniden kullanılsaydı bir belgeye ait elle taşıma talimatı ya
        da hata kutusu, BAŞKA bir belgenin ekranında asılı kalırdı.
      */}
      {selected !== null && (
        <TransmissionDetail key={selected} invoiceId={selected} readOnly={readOnly} />
      )}
    </>
  );
}

function QueueRow({
  invoice,
  view,
  loading,
  selected,
  onSelect,
}: {
  invoice: SalesInvoice;
  view: EinvoiceTransmissionView | null;
  loading: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const open = daysOpen(invoice.issueDate);
  const active = view?.active ?? null;

  return (
    <tr
      onClick={onSelect}
      className={
        'cursor-pointer transition-colors ' +
        (selected ? 'bg-[var(--c-brand-soft)]' : 'hover:bg-[var(--c-surface-alt)]')
      }
    >
      <Td>
        {/*
          Satırın tamamı tıklanabilir, ama seçim KLAVYEYLE de yapılabilmeli:
          `<tr onClick>` odaklanamaz. Numara bu yüzden gerçek bir düğmedir
          (`Contacts.tsx`te aynı kalıbın bağlantılı hâli var).
        */}
        <button
          type="button"
          aria-pressed={selected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className="tabular font-medium text-[var(--c-brand)] hover:underline"
        >
          {invoice.documentNumber}
        </button>
        <span className="block text-xs text-[var(--c-ink-faint)]">
          {DOCUMENT_TYPE_LABELS[invoice.documentType]} · {STATUS_LABELS[invoice.status]}
        </span>
      </Td>
      <Td>{invoice.billingName}</Td>
      <Td>{date(invoice.issueDate)}</Td>
      <Td align="right">
        {/* Gün sayısı bir SÜRE ölçüsüdür, bir hukuki süre değil: uzun bekleyen
            belge riskin BÜYÜKLÜĞÜNÜ gösterir, bir son tarihi değil. */}
        {open === null ? '—' : `${open} gün`}
      </Td>
      <Td align="right">{money(invoice.totalInclVat)}</Td>
      <Td>
        {active === null ? (
          loading && view === null ? (
            <Spinner size={14} />
          ) : (
            <Badge tone="neutral">Hazırlanmadı</Badge>
          )
        ) : (
          <>
            <Badge tone={EINVOICE_STATUS_TONES[active.status]}>
              {EINVOICE_STATUS_LABELS[active.status]}
            </Badge>
            {active.attemptCount > 0 && (
              <span className="ml-2 text-xs text-[var(--c-ink-faint)]">
                {active.attemptCount} deneme
              </span>
            )}
          </>
        )}
      </Td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────── detay + eylemler

function TransmissionDetail({ invoiceId, readOnly }: { invoiceId: string; readOnly: boolean }) {
  const query = useEinvoiceTransmission(invoiceId);
  const view = query.data ?? null;

  const prepare = usePrepareEinvoice();
  const send = useSendEinvoice();
  const markManual = useMarkSentManually();
  const abandon = useAbandonEinvoice();

  /*
   * Eylem hatası TOAST'a bırakılmaz: `EINVOICE_LOCAL_VALIDATION_FAILED` bir
   * KURAL KODU listesi taşır (`fields`) ve o kodlar, sağlayıcının ret
   * mesajıyla eşleştirilebilecek tek anahtardır. Kaybolan bir bildirim bunu
   * taşıyamaz.
   */
  const [actionError, setActionError] = useState<unknown>(null);
  const [handoff, setHandoff] = useState<PeppolManualHandoff | null>(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [downloading, setDownloading] = useState(false);

  if (query.isPending) {
    return (
      <Card>
        <div className="flex justify-center py-10">
          <Spinner size={24} />
        </div>
      </Card>
    );
  }
  if (query.isError || view === null) {
    return (
      <Card>
        <ErrorState message={errorMessage(query.error)} detail={errorDetail(query.error)} />
      </Card>
    );
  }

  const active = view.active;
  const others = view.documents.filter((d) => d.id !== active?.id);
  const busy = prepare.isPending || send.isPending || markManual.isPending || abandon.isPending;

  /** Ayar KAPALI: otomatik gönderim hiç denenmez (sunucudaki mod kapısıyla aynı). */
  const modeOff = view.mode === 'OFF';
  const canSend = active !== null && SENDABLE_STATUSES.includes(active.status);
  const canMarkManual = active !== null && MANUAL_MARKABLE_STATUSES.includes(active.status);
  const canAbandon = active !== null && ABANDONABLE_STATUSES.includes(active.status);

  async function download(acknowledgeResend: boolean) {
    setDownloading(true);
    try {
      const file = await downloadEinvoiceXml(invoiceId, acknowledgeResend);
      /*
       * Ad SUNUCUNUNDUR (`Content-Disposition`). Yedek ad yalnız başlık hiç
       * gelmediğinde kullanılır ve ASCII'ye indirgenir: belge numarası
       * operatörün düzenleyebildiği bir biçimden gelir ve `/` içerebilir.
       */
      const fallback = `${(view?.documentNumber ?? invoiceId).replace(/[^A-Za-z0-9._-]/g, '_')}.xml`;
      saveBlob(file.blob, file.fileName ?? fallback);
      setResendOpen(false);
      toast.success('XML indirildi.');
    } catch (err) {
      if (hasCode(err, 'EINVOICE_ALREADY_DELIVERED')) {
        // Kapı: teslim edilmiş belgede indirme AÇIK ONAY ister.
        setResendOpen(true);
        return;
      }
      toast.error(err);
    } finally {
      setDownloading(false);
    }
  }

  function runPrepare() {
    setActionError(null);
    prepare.mutate(invoiceId, {
      onSuccess: (res) =>
        toast.success(
          res.reused
            ? 'Baytlar zaten hazırdı; yeniden üretilmedi.'
            : 'Baytlar üretildi ve arşivlendi. Belge HENÜZ İLETİLMEDİ.',
        ),
      onError: setActionError,
    });
  }

  function runSend() {
    setActionError(null);
    setHandoff(null);
    send.mutate(invoiceId, {
      onSuccess: (res) => {
        /*
         * ⚠ 200 "iletildi" DEMEK DEĞİLDİR. `MANUAL_REQUIRED`ta satır `PREPARED`
         * kalır ve taşımayı insan yapar; burada "gönderildi" yazmak, hiç
         * iletilmemiş faturaları gönderilmiş göstermek olurdu.
         */
        if (res.outcome === 'MANUAL_REQUIRED') {
          setHandoff(res.handoff);
          toast.info('Otomatik gönderim yapılmadı: taşımayı siz yapacaksınız.');
        } else if (res.outcome === 'SUBMITTED') {
          toast.success('Sağlayıcı belgeyi taşımaya ALDI. Bu bir söz; teslim henüz onaylanmadı.');
        } else {
          toast.success('Belge elle gönderilmiş olarak kayıtlı.');
        }
        for (const code of res.warnings) toast.errorText(errorMessage({ code }));
      },
      onError: setActionError,
    });
  }

  function runMarkManual() {
    setActionError(null);
    markManual.mutate(
      { invoiceId, reference: reference.trim() },
      {
        onSuccess: (res) => {
          setManualOpen(false);
          setReference('');
          toast.success('Beyan kaydedildi: belge elle gönderildi olarak işaretlendi.');
          for (const code of res.warnings) toast.errorText(errorMessage({ code }));
        },
        onError: (err) => {
          setManualOpen(false);
          setActionError(err);
        },
      },
    );
  }

  function runAbandon() {
    setActionError(null);
    abandon.mutate(
      { invoiceId, reason: reason.trim() },
      {
        onSuccess: () => {
          setAbandonOpen(false);
          setReason('');
          toast.success('Taşıma satırı kapatıldı: vazgeçildi.');
        },
        onError: (err) => {
          setAbandonOpen(false);
          setActionError(err);
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader
        title={`${view.documentNumber ?? 'Numarasız belge'} — taşıma`}
        description={
          view.obligationOpen
            ? 'Bu belgenin iletim yükümlülüğü AÇIK: henüz hiçbir iletim mühürlenmedi.'
            : 'Bu belgenin yükümlülüğü bir iletim belgesiyle kapatıldı.'
        }
      />

      <div className="grid gap-x-6 gap-y-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Peppol modu (ayar)">
          {PEPPOL_MODE_LABELS[view.mode]}
          <span className="block text-xs text-[var(--c-ink-faint)]">
            {PEPPOL_MODE_MEANINGS[view.mode]}
          </span>
        </Fact>
        <Fact label="Hedef ağ">{EINVOICE_NETWORK_LABELS[view.network]}</Fact>
        <Fact label="Taşıma yolu">{EINVOICE_TRANSPORT_KIND_LABELS[view.transportKind]}</Fact>
        <Fact label="Sağlayıcı">{view.providerName}</Fact>
        <Fact label="Yükümlülük dayanağı">
          {view.einvoiceObligationBasis === null
            ? 'Yapısal e-fatura zorunluluğu yok'
            : (EINVOICE_OBLIGATION_BASIS_LABELS[view.einvoiceObligationBasis] ??
              view.einvoiceObligationBasis)}
        </Fact>
        <Fact label="Fatura">
          <Link
            className="text-[var(--c-brand)] hover:underline"
            to={`/invoices/${view.invoiceId}`}
          >
            Belgeyi aç
          </Link>
        </Fact>
      </div>

      {actionError !== null && <ActionError error={actionError} />}

      {handoff !== null && (
        <div className="mx-4 mb-3 rounded-[var(--r-md)] border border-[var(--c-warn)] bg-[var(--c-warn-soft)] px-3 py-2 text-sm">
          {/* Talimat metni SUNUCUDA üretiliyor (`buildManualInstruction`) ve
              olduğu gibi gösteriliyor: ikinci bir kopya yazmak, ağ etiketini
              söyleyen cümlenin panelde eskimesi demek olurdu. */}
          <pre className="whitespace-pre-wrap font-sans">{handoff.instruction}</pre>
        </div>
      )}

      {active === null ? (
        <EmptyState
          title="Bu ağda hazırlanmış bayt yok"
          description="Hazırlık tamamen yereldir: baytlar üretilir, doğrulanır ve arşivlenir — ağa çıkılmaz."
        />
      ) : (
        <ActiveDocument document={active} />
      )}

      <footer className="flex flex-wrap items-center gap-2 border-t border-[var(--c-border)] px-4 py-3">
        {!readOnly && active === null && (
          <Button loading={prepare.isPending} disabled={busy} onClick={runPrepare}>
            Hazırla
          </Button>
        )}
        {!readOnly && canSend && (
          /*
           * ⚠ Mod KAPALIYKEN düğme KAPALI ve cümle sunucununkiyle AYNI
           * (`messageForCode`). Açık bırakılsaydı her tıklama, ağa hiç
           * çıkmayan ama olay kaydına CONFIG arızası yazan bir istek üretirdi;
           * operatör de aynı hatayı üç kez okurdu. Panel kuralı erken sayarken
           * sunucunun cümlesini kullanır — iki farklı metin, tek kuralı iki
           * ayrı arıza sanmaya yol açar.
           */
          <Button loading={send.isPending} disabled={busy || modeOff} onClick={runSend}>
            Gönder
          </Button>
        )}
        {active !== null && (
          <Button
            variant="secondary"
            loading={downloading}
            disabled={busy}
            onClick={() => void download(false)}
          >
            XML indir
          </Button>
        )}
        {!readOnly && canMarkManual && (
          <Button variant="secondary" disabled={busy} onClick={() => setManualOpen(true)}>
            Elle gönderildi
          </Button>
        )}
        {!readOnly && canAbandon && (
          <Button variant="danger" disabled={busy} onClick={() => setAbandonOpen(true)}>
            Vazgeç
          </Button>
        )}
        {modeOff && (
          <span className="text-xs text-[var(--c-ink-soft)]">
            {messageForCode('EINVOICE_MODE_OFF')}
          </span>
        )}
      </footer>

      {others.length > 0 && <OtherDocuments documents={others} />}

      {/* ── XML'i YENİDEN indirme onayı ─────────────────────────────────── */}
      <Modal
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        title="Bu belge zaten iletildi"
        footer={
          <>
            <Button variant="secondary" onClick={() => setResendOpen(false)}>
              Vazgeç
            </Button>
            <Button variant="danger" loading={downloading} onClick={() => void download(true)}>
              Yine de indir
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Elle rejimde <strong>indirmek teslimatın kendisidir</strong>: dosyayı sağlayıcı portalına
          ikinci kez yüklerseniz alıcının sisteminde <strong>ikinci bir borç kaydı</strong> doğar ve
          bunu hiçbir şey geri almaz.
        </p>
        <p className="mt-2 text-sm text-[var(--c-ink-soft)]">
          Baytlara arşiv ya da denetim amacıyla bakmak meşrudur; onay yalnız kararın bilerek
          verilmesi için isteniyor.
        </p>
      </Modal>

      {/* ── "Elle gönderildi" beyanı ────────────────────────────────────── */}
      <Modal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        title="Elle gönderildi olarak işaretle"
        footer={
          <>
            <Button variant="secondary" onClick={() => setManualOpen(false)}>
              Vazgeç
            </Button>
            <Button
              variant="danger"
              loading={markManual.isPending}
              disabled={reference.trim() === ''}
              onClick={runMarkManual}
            >
              Beyanı kaydet
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Bu bir <strong>beyandır</strong>, bir tahmin değil: baytları portala siz yüklediniz.
        </p>
        <p className="mt-2 text-sm text-[var(--c-danger)]">
          ⚠ GERİ ALINAMAZ. Kayıt bir kez yazılır (kim, ne zaman, hangi referansla), durum
          terminaldir ve gerçek ağda faturanın yükümlülüğü <strong>kalıcı olarak</strong> kapanır.
          Yanlış işaretlenen bir belge, "iletilmemiş belgeler" listesinde bir daha asla görünmez.
        </p>
        <div className="mt-3">
          <Input
            label="Portal referansı (zorunlu)"
            hint="Sağlayıcı portalının verdiği yükleme/iletim numarası. Referanssız bir beyan denetimde hiçbir şey ifade etmez."
            value={reference}
            maxLength={200}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
      </Modal>

      {/* ── Vazgeçme ────────────────────────────────────────────────────── */}
      <Modal
        open={abandonOpen}
        onClose={() => setAbandonOpen(false)}
        title="Bu belgeden vazgeç"
        footer={
          <>
            {/* İptal düğmesi burada "Vazgeç" DİYEMEZ: eylemin adı da vazgeçmek.
                İki düğmenin aynı sözcüğü taşıması, kapatmak isterken yükümlülüğü
                kapatmaya götürürdü. */}
            <Button variant="secondary" onClick={() => setAbandonOpen(false)}>
              Kapat
            </Button>
            <Button
              variant="danger"
              loading={abandon.isPending}
              disabled={reason.trim() === ''}
              onClick={runAbandon}
            >
              Taşımayı kapat
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Vazgeçme <strong>terminaldir</strong>: bu satır bir daha gönderilemez. Deneme tavanı
          aşıldığında bile hiçbir kod yolu bunu kendiliğinden yapmaz — bir yükümlülüğü kapatan karar
          insanındır.
        </p>
        <div className="mt-3">
          <Textarea
            label="Gerekçe (zorunlu)"
            hint='"Neden gönderilmedi" sorusunun cevabı yalnızca burada kalır; salt-ekleme olay kaydına yazılır.'
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </Modal>
    </Card>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="block text-xs text-[var(--c-ink-faint)]">{label}</span>
      <span className="block text-sm text-[var(--c-ink)]">{children}</span>
    </div>
  );
}

/**
 * Eylem hatası — kural kodlarıyla birlikte.
 *
 * ⚠ `EINVOICE_LOCAL_VALIDATION_FAILED` yanıtında `fields`, alan adı DEĞİL KURAL
 * KODU listesidir (`BR-CO-13`, `PEPPOL-EN16931-R003`…). Sunucunun serbest
 * metnini göstermiyoruz (dil bağımsız değil), ama kodları göstermek zorundayız:
 * erişim noktası belgeyi reddettiğinde mesajında aynı kodlar geçer ve operatör
 * için tek eşleştirme anahtarı odur.
 */
function ActionError({ error }: { error: unknown }) {
  const rules = [...errorFields(error)];
  return (
    <div className="mx-4 mb-3 rounded-[var(--r-md)] border border-[var(--c-danger)] px-3 py-2">
      <p className="text-sm text-[var(--c-danger)]">{errorMessage(error)}</p>
      {rules.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {rules.map((rule) => (
            <li key={rule} className="font-mono text-xs text-[var(--c-ink-soft)]">
              {rule}
            </li>
          ))}
        </ul>
      )}
      {errorDetail(error) !== null && (
        <p className="mt-1 font-mono text-[11px] text-[var(--c-ink-faint)]">{errorDetail(error)}</p>
      )}
    </div>
  );
}

function ActiveDocument({ document }: { document: EinvoiceDocument }) {
  return (
    <div className="border-t border-[var(--c-border)] px-4 py-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge tone={EINVOICE_STATUS_TONES[document.status]}>
          {EINVOICE_STATUS_LABELS[document.status]}
        </Badge>
        <span className="text-sm text-[var(--c-ink-soft)]">
          {EINVOICE_STATUS_MEANINGS[document.status]}
        </span>
      </div>

      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Hazırlandı">{dateTime(document.preparedAt)}</Fact>
        <Fact label="Durum değişti">{dateTime(document.statusChangedAt)}</Fact>
        <Fact label="Deneme sayısı">{document.attemptCount}</Fact>
        <Fact label="Son deneme">{dateTime(document.lastAttemptAt)}</Fact>
        <Fact label="Sonraki deneme">{dateTime(document.nextAttemptAt)}</Fact>
        <Fact label="Sağlayıcı aldı">{dateTime(document.submittedAt)}</Fact>
        <Fact label="Ağ teslim etti">{dateTime(document.deliveredAt)}</Fact>
        <Fact label="Sağlayıcı iletim kimliği">
          <span className="font-mono text-xs">{document.providerMessageId ?? '—'}</span>
        </Fact>
        <Fact label="Gönderici (taşıma)">
          <span className="font-mono text-xs">{document.senderParticipantId}</span>
        </Fact>
        <Fact label="Alıcı (taşıma)">
          <span className="font-mono text-xs">{document.recipientParticipantId}</span>
        </Fact>
        <Fact label="Alıcı araması">
          {document.recipientLookupResult === null
            ? '—'
            : EINVOICE_LOOKUP_RESULT_LABELS[document.recipientLookupResult]}
          {document.recipientLookupAt !== null && (
            <span className="block text-xs text-[var(--c-ink-faint)]">
              {dateTime(document.recipientLookupAt)}
            </span>
          )}
        </Fact>
        <Fact label="Ağ etiketi (donmuş)">{EINVOICE_NETWORK_LABELS[document.network]}</Fact>
      </div>

      {document.failureClass !== null && (
        <div className="mt-3 rounded-[var(--r-md)] border border-[var(--c-border-strong)] px-3 py-2">
          <p className="text-sm font-medium">
            {EINVOICE_FAILURE_CLASS_LABELS[document.failureClass]}
            {document.failureCode !== null && (
              <span className="ml-2 font-mono text-xs text-[var(--c-ink-soft)]">
                {document.failureCode}
              </span>
            )}
            {document.failedAt !== null && (
              <span className="ml-2 text-xs text-[var(--c-ink-faint)]">
                {dateTime(document.failedAt)}
              </span>
            )}
          </p>
          {document.failureMessage !== null && (
            /*
             * ⚠ SAĞLAYICININ METNİNİN OKUNABİLDİĞİ TEK YER BURASI: geçici arıza
             * 503 döner ve sunucu 5xx'te `message`i düşürür. Metinde geçen kural
             * kodu (`BR-CO-13` gibi), yerel doğrulayıcımızın bulgularıyla
             * eşleştirilebilecek tek anahtardır — kırpılmaz.
             */
            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap font-mono text-xs text-[var(--c-ink-soft)]">
              {document.failureMessage}
            </pre>
          )}
        </div>
      )}

      {document.businessResponseStatus !== null && (
        <div className="mt-3 rounded-[var(--r-md)] border border-[var(--c-border-strong)] px-3 py-2">
          <p className="text-sm font-medium">
            Alıcının iş yanıtı: {EINVOICE_BUSINESS_RESPONSE_LABELS[document.businessResponseStatus]}
            {document.businessResponseCodeRaw !== null && (
              <span className="ml-2 font-mono text-xs text-[var(--c-ink-soft)]">
                {document.businessResponseCodeRaw}
              </span>
            )}
          </p>
          {/* ⚠ İş reddi TAŞIMAYI DEĞİŞTİRMEZ: belge teslim edilmiştir ve çare
              ticaridir. Aynı kutuda gösterilseydi operatör teslim edilmiş bir
              faturayı yeniden gönderirdi. */}
          <p className="mt-1 text-xs text-[var(--c-ink-soft)]">
            Bu yanıt taşımayı değiştirmez: belge iletilmiştir. Ticari itirazın yolu kredi notudur.
          </p>
          {document.businessResponseReason !== null && (
            <p className="mt-1 text-xs text-[var(--c-ink-soft)]">
              {document.businessResponseReason}
            </p>
          )}
        </div>
      )}

      {document.manualSentAt !== null && (
        <div className="mt-3 rounded-[var(--r-md)] border border-[var(--c-border-strong)] px-3 py-2 text-sm">
          <span className="font-medium">Elle taşıma beyanı</span>
          <span className="ml-2 text-[var(--c-ink-soft)]">{dateTime(document.manualSentAt)}</span>
          <span className="ml-2 font-mono text-xs">{document.manualReference}</span>
        </div>
      )}

      {/* Arşiv: "ne gönderdik" sorusunun tek cevabı bu baytlardır; özet
          dosyanın KİMLİĞİNİ kanıtlar, otantikliğini değil. */}
      <div className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Arşiv yolu">
          <span className="font-mono text-xs break-all">{document.xmlStoragePath}</span>
        </Fact>
        <Fact label="Bayt sayısı">{document.xmlByteLength}</Fact>
        <Fact label="sha256">
          <span className="font-mono text-xs break-all">{document.xmlSha256}</span>
        </Fact>
        <Fact label="Eşleyici / profil">
          <span className="font-mono text-xs break-all">
            {document.mapperVersion} · {document.ublCustomizationId}
          </span>
        </Fact>
      </div>
    </div>
  );
}

/**
 * Diğer satırlar: supersede edilmişler ve DİĞER ağın satırları.
 *
 * ⚠ Gizlenmemeleri gerekir: bir belgenin TEST ağında "gönderilmiş" bir satırı
 * olabilir ve o satır yasal olarak hiçbir şey ifade etmez. Yalnız aktif satır
 * gösterilseydi, operatör TEST kaydını gerçek iletim sanabilirdi.
 */
function OtherDocuments({ documents }: { documents: EinvoiceDocument[] }) {
  return (
    <div className="border-t border-[var(--c-border)]">
      <CardHeader
        title="Diğer taşıma satırları"
        description="Supersede edilmiş satırlar ve diğer ağın kayıtları. TEST ağındaki bir kayıt yasal iletim DEĞİLDİR."
      />
      <Table>
        <thead>
          <tr>
            <Th>Ağ</Th>
            <Th>Durum</Th>
            <Th>Hazırlandı</Th>
            <Th>Supersede</Th>
          </tr>
        </thead>
        <tbody>
          {documents.map((d) => (
            <tr key={d.id}>
              <Td>{EINVOICE_NETWORK_LABELS[d.network]}</Td>
              <Td>
                <Badge tone={EINVOICE_STATUS_TONES[d.status]}>
                  {EINVOICE_STATUS_LABELS[d.status]}
                </Badge>
              </Td>
              <Td>{dateTime(d.preparedAt)}</Td>
              <Td>
                {d.supersededAt === null ? (
                  '—'
                ) : (
                  <>
                    {dateTime(d.supersededAt)}
                    {d.supersedeReason !== null && (
                      <span className="block text-xs text-[var(--c-ink-faint)]">
                        {d.supersedeReason}
                      </span>
                    )}
                  </>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ────────────────────────────────────────────────────── sekme 2: gelen kutusu

const INCOMING_STATUS_LABELS: Record<string, string> = {
  RECEIVED: 'işlenmedi',
  PROCESSED: 'taslak alışa çevrildi',
  PARSE_FAILED: 'okunamadı',
  IGNORED: 'bize ait değil',
};

const INCOMING_STATUS_TONES: Record<string, 'neutral' | 'ok' | 'warn' | 'danger'> = {
  RECEIVED: 'warn',
  PROCESSED: 'ok',
  PARSE_FAILED: 'danger',
  IGNORED: 'neutral',
};

const INCOMING_KIND_LABELS: Record<string, string> = {
  UNCLASSIFIED: 'sınıflanmamış',
  INVOICE: 'fatura',
  CREDIT_NOTE: 'alacak dekontu',
  OTHER: 'diğer',
};

/**
 * Gelen kutusu — belgenin İKİ giriş yolu tek listede.
 *
 * ⚠ ASIL YOL BUGÜN YÜKLEMEDİR. Peppol aboneliği olmadığı sürece sağlayıcının
 * imzalı webhook'u hiç çalmaz; tedarikçi faturası e-postayla gelir ve buradan
 * yüklenir. Satırdaki "geliş" sütunu ikisini AYIRIR: imzalı bir teslimatın
 * kimliği ağ tarafından doğrulanmıştır, elle yüklenen dosyanınki YÜKLEYEN
 * tarafından seçilmiştir — aynı güven değil.
 *
 * ⚠ TUTAR, KDV ya da "indirilebilir KDV" BURADA GÖSTERİLMEZ. XML'den okunmuş
 * bir toplamı listede göstermek, operatörün onu DOĞRULANMIŞ bir rakam
 * sanmasına yeter; oysa o rakam ne bir onaydan ne bir doğrulayıcıdan
 * geçmiştir. Rakamlar taslak alışa çevrildikten SONRA, onay akışının içinde
 * görünür.
 *
 * ⚠ "Taslak alışa çevir" TASLAK üretir, kayıt değil: tutarlar tedarikçinin
 * XML'inden okunur ama indirim oranları ve kutu kararı operatörün onayıyla
 * donar (`purchases.service.ts#createFromIncoming`).
 */
function InboxTab({ canReadSettings, readOnly }: { canReadSettings: boolean; readOnly: boolean }) {
  const inbox = useEinvoiceInbox();

  return (
    <div className="space-y-4">
      <UploadIncomingCard readOnly={readOnly} />

      <Card>
        <CardHeader
          title="Gelen belgeler"
          description="En yeni üstte. Bir belgeden en çok BİR alış faturası doğar."
        />

        {inbox.isPending ? (
          <div className="px-4 py-10 text-center">
            <Spinner />
          </div>
        ) : inbox.isError ? (
          <ErrorState message={errorMessage(inbox.error)} detail={errorDetail(inbox.error)} />
        ) : inbox.data.items.length === 0 ? (
          <EmptyState
            title="Gelen belge yok"
            description="Tedarikçinin e-postayla gönderdiği XML ekini yukarıdan yükleyin."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Alındı</Th>
                <Th>Geliş</Th>
                <Th>Tür</Th>
                <Th>Boyut</Th>
                <Th>Durum</Th>
                <Th>Eylem</Th>
              </tr>
            </thead>
            <tbody>
              {inbox.data.items.map((doc) => (
                <IncomingRow key={doc.id} doc={doc} readOnly={readOnly} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <p className="text-xs text-[var(--c-ink-faint)]">
        {canReadSettings
          ? 'Sağlayıcının imzalı webhook’u ayrı bir yoldur ve yalnız imza sırrı girilmişken kabul edilir; sırrın girilip girilmediği yukarıdaki bantta yazar.'
          : 'Sağlayıcının imzalı webhook yapılandırmasını yalnızca yönetici görebilir.'}
      </p>
    </div>
  );
}

/**
 * Tedarikçinin gönderdiği UBL'i yükler.
 *
 * ⚠ PDF DEĞİL, XML. Tedarikçi e-postasında ikisi de bulunur; taslak ancak
 * yapılandırılmış olandan üretilebilir. Sunucu dosyayı ÖNCE okur, UBL değilse
 * arşive hiç yazmaz — bu yüzden yanlış dosya seçmek bir kalıntı bırakmaz.
 */
function UploadIncomingCard({ readOnly }: { readOnly: boolean }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const upload = useUploadIncomingUbl();
  const [error, setError] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader
        title="Tedarikçi faturası yükle"
        description="E-postayla gelen UBL (XML) dosyasını gelen kutusuna koyar. Aynı dosya ikinci kez yüklenirse yeni kayıt doğmaz."
      />

      <div className="space-y-3 px-4 py-3 text-sm">
        {/* ⚠ HAM BAYT, multipart DEĞİL: sunucu dosya adını hiçbir yolda
            kullanmıyor; arşivdeki ad baytların özetinden türer. */}
        <input
          ref={fileInput}
          type="file"
          accept=".xml,application/xml,text/xml"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            setError(null);
            upload.mutate(file, {
              onSuccess: (r) =>
                toast.success(
                  r.status === 'DUPLICATE'
                    ? 'Bu dosya zaten yüklenmişti; var olan kayıt kullanılacak.'
                    : 'Belge gelen kutusuna eklendi.',
                ),
              onError: (err: unknown) => setError(errorMessage(err)),
            });
          }}
        />

        <div className="flex items-center gap-2">
          <Button
            disabled={readOnly || upload.isPending}
            onClick={() => fileInput.current?.click()}
          >
            UBL (XML) dosyası seç
          </Button>
          {upload.isPending && <Spinner />}
        </div>

        {readOnly && (
          <p className="text-[var(--c-ink-soft)]">Yetkiniz salt okunur; yükleme yapamazsınız.</p>
        )}

        {error !== null && <p className="text-[var(--c-danger)]">{error}</p>}

        <p className="text-xs text-[var(--c-ink-faint)]">
          ⚠ Tedarikçinin e-postasındaki <strong>PDF değil, XML</strong> ekini seçin: taslak alış
          ancak yapılandırılmış belgeden üretilebilir. PDF’i belge eki olarak Alışlar ekranından
          ekleyebilirsiniz.
        </p>
      </div>
    </Card>
  );
}

function IncomingRow({ doc, readOnly }: { doc: IncomingDocument; readOnly: boolean }) {
  const convert = useCreatePurchaseFromIncoming();
  const [error, setError] = useState<string | null>(null);
  const uploaded = doc.verificationMethod === 'OPERATOR_UPLOAD';

  return (
    <tr>
      <Td>{dateTime(doc.receivedAt)}</Td>
      <Td>
        {INCOMING_VERIFICATION_LABELS[doc.verificationMethod] ?? doc.verificationMethod}
        <span className="block text-xs text-[var(--c-ink-faint)]">
          {uploaded ? 'panelden' : `${doc.providerName ?? 'sağlayıcı'} · ${doc.network}`}
        </span>
      </Td>
      <Td>{INCOMING_KIND_LABELS[doc.documentKind] ?? doc.documentKind}</Td>
      <Td>{`${String(Math.max(1, Math.round(doc.payloadByteLength / 1024)))} KB`}</Td>
      <Td>
        <Badge tone={INCOMING_STATUS_TONES[doc.processingStatus] ?? 'neutral'}>
          {INCOMING_STATUS_LABELS[doc.processingStatus] ?? doc.processingStatus}
        </Badge>
        {doc.processingNote !== null && (
          <span className="block max-w-[28ch] truncate text-xs text-[var(--c-ink-faint)]">
            {doc.processingNote}
          </span>
        )}
      </Td>
      <Td>
        {doc.processingStatus === 'PROCESSED' ? (
          <Link className="text-[var(--c-brand)] hover:underline" to="/purchases">
            Alışlarda aç
          </Link>
        ) : (
          <div className="space-y-1">
            <Button
              variant="secondary"
              size="sm"
              disabled={readOnly || convert.isPending}
              onClick={() => {
                setError(null);
                convert.mutate(doc.id, {
                  onSuccess: () => toast.success('Taslak alış faturası üretildi.'),
                  onError: (err: unknown) => setError(errorMessage(err)),
                });
              }}
            >
              Taslak alışa çevir
            </Button>
            {error !== null && (
              <span className="block max-w-[40ch] text-xs text-[var(--c-danger)]">{error}</span>
            )}
          </div>
        )}
      </Td>
    </tr>
  );
}
